"use client";

import { useEffect, useId, useRef, useState } from "react";
import { useTranslations } from "next-intl";

import { ApiError, apiClient } from "@/lib/api";
import type { components } from "@/lib/api/generated/wio-veyra-api";

type LoginContext = components["schemas"]["LoginContextResponse"];
type LoginRequest = components["schemas"]["LoginRequest"];
type LoginResponse = components["schemas"]["LoginResponse"];
type DevelopmentLoginRequest = components["schemas"]["DevelopmentLoginRequest"];
type FeedbackKey =
  "invalidCredentials" | "rateLimited" | "unavailable" | "outcomeUnknown";
type SubmissionKind = "credentials" | "development";

export function LoginForm({ returnTo }: { returnTo?: string }) {
  const t = useTranslations("publicAccess.login");
  const emailId = useId();
  const passwordId = useId();
  const requestRef = useRef<AbortController | null>(null);
  const [context, setContext] = useState<LoginContext>();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [feedback, setFeedback] = useState<FeedbackKey>();
  const [submission, setSubmission] = useState<SubmissionKind>();
  const isSubmitting = submission !== undefined;

  useEffect(() => {
    const controller = new AbortController();
    requestRef.current = controller;
    void loadContext(returnTo, controller.signal).then((result) => {
      if (!result) return;
      if (result.isAuthenticated) window.location.assign(result.nextPath);
      else setContext(result);
    });
    return () => controller.abort();
  }, [returnTo]);

  async function loadContext(
    requestedPath: string | undefined,
    signal?: AbortSignal,
  ): Promise<LoginContext | undefined> {
    const suffix = requestedPath
      ? `?returnTo=${encodeURIComponent(requestedPath)}`
      : "";
    try {
      const response = await apiClient.request<LoginContext>({
        operation: "GetLoginContext",
        kind: "query",
        method: "GET",
        path: `/identity/context${suffix}`,
        signal,
      });
      return response.data;
    } catch (error) {
      if (error instanceof ApiError && error.kind === "cancelled") return;
      setFeedback("unavailable");
    }
  }

  async function submit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!context || isSubmitting) return;
    setSubmission("credentials");
    setFeedback(undefined);
    const controller = new AbortController();
    requestRef.current?.abort();
    requestRef.current = controller;
    try {
      const response = await sendLogin(context, controller.signal);
      window.location.assign(response.nextPath);
    } catch (error) {
      await handleLoginError(error, context.nextPath, controller.signal);
    } finally {
      setSubmission(undefined);
    }
  }

  async function submitDevelopmentLogin() {
    if (!context?.developmentQuickLoginEnabled || isSubmitting) return;
    setSubmission("development");
    setFeedback(undefined);
    const controller = new AbortController();
    requestRef.current?.abort();
    requestRef.current = controller;
    try {
      const response = await sendDevelopmentLogin(context, controller.signal);
      window.location.assign(response.nextPath);
    } catch (error) {
      await handleLoginError(error, context.nextPath, controller.signal);
    } finally {
      setSubmission(undefined);
    }
  }

  async function sendLogin(
    loginContext: LoginContext,
    signal: AbortSignal,
  ): Promise<LoginResponse> {
    const body: LoginRequest = {
      email,
      password,
      nextPath: loginContext.nextPath,
    };
    const response = await apiClient.request<LoginResponse, LoginRequest>({
      operation: "Login",
      kind: "command",
      method: "POST",
      path: "/identity/login",
      body,
      antiforgeryToken: loginContext.antiforgeryToken,
      signal,
    });
    return response.data;
  }

  async function sendDevelopmentLogin(
    loginContext: LoginContext,
    signal: AbortSignal,
  ): Promise<LoginResponse> {
    const body: DevelopmentLoginRequest = { nextPath: loginContext.nextPath };
    const response = await apiClient.request<
      LoginResponse,
      DevelopmentLoginRequest
    >({
      operation: "DevelopmentLogin",
      kind: "command",
      method: "POST",
      path: "/identity/development-login",
      body,
      antiforgeryToken: loginContext.antiforgeryToken,
      signal,
    });
    return response.data;
  }

  async function handleLoginError(
    error: unknown,
    nextPath: string,
    signal: AbortSignal,
  ) {
    setPassword("");
    if (!(error instanceof ApiError)) return setFeedback("unavailable");
    if (error.status === 429) return setFeedback("rateLimited");
    if (error.kind === "unauthorized") return setFeedback("invalidCredentials");
    if (error.kind !== "outcome-unknown") return setFeedback("unavailable");
    const reconciled = await loadContext(returnTo, signal);
    if (reconciled?.isAuthenticated) window.location.assign(nextPath);
    else setFeedback("outcomeUnknown");
  }

  return (
    <form
      method="post"
      data-slot="login-form"
      data-debug-source="src/components/auth/login-form.tsx"
      className="mt-8 space-y-5"
      onSubmit={submit}
      noValidate={false}
    >
      <div className="space-y-2">
        <label
          htmlFor={emailId}
          className="text-sm font-medium text-foreground"
        >
          {t("email.label")}
        </label>
        <input
          id={emailId}
          name="email"
          type="email"
          inputMode="email"
          autoComplete="username"
          required
          maxLength={320}
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          placeholder={t("email.placeholder")}
          className="min-h-12 w-full rounded-md border border-input bg-background px-3 text-base text-foreground shadow-sm placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-60 sm:text-sm"
          disabled={!context || isSubmitting}
        />
      </div>

      <div className="space-y-2">
        <label
          htmlFor={passwordId}
          className="text-sm font-medium text-foreground"
        >
          {t("password.label")}
        </label>
        <div className="relative">
          <input
            id={passwordId}
            name="password"
            type={showPassword ? "text" : "password"}
            autoComplete="current-password"
            required
            maxLength={1024}
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            placeholder={t("password.placeholder")}
            className="min-h-12 w-full rounded-md border border-input bg-background px-3 pe-12 text-base text-foreground shadow-sm placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-60 sm:text-sm"
            disabled={!context || isSubmitting}
          />
          <button
            type="button"
            disabled={!context || isSubmitting}
            onClick={() => setShowPassword((visible) => !visible)}
            className="absolute inset-y-0 end-0 grid w-12 place-items-center text-muted-foreground hover:text-foreground"
            aria-label={showPassword ? t("password.hide") : t("password.show")}
          >
            <svg
              viewBox="0 0 24 24"
              className="size-5"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              aria-hidden="true"
            >
              <path d="M2 12s3.5-6 10-6 10 6 10 6-3.5 6-10 6S2 12 2 12Z" />
              <circle cx="12" cy="12" r="2.5" />
            </svg>
          </button>
        </div>
      </div>

      {feedback ? (
        <p
          role="alert"
          className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm leading-6 text-foreground"
        >
          {t(`feedback.${feedback}`)}
        </p>
      ) : null}

      <button
        type="submit"
        disabled={!context || isSubmitting}
        className="min-h-12 w-full rounded-md bg-primary px-4 text-sm font-semibold text-primary-foreground shadow-sm transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-60"
      >
        {!context
          ? t("loading")
          : submission === "credentials"
            ? t("submitting")
            : t("submit")}
      </button>

      {context?.developmentQuickLoginEnabled ? (
        <section
          className="border-t border-border pt-5"
          aria-labelledby="quick-login-title"
        >
          <div className="mb-3 flex items-center justify-between gap-3">
            <div>
              <h2
                id="quick-login-title"
                className="text-sm font-semibold text-foreground"
              >
                {t("quickLogin.title")}
              </h2>
              <p className="mt-1 text-sm leading-5 text-muted-foreground">
                {t("quickLogin.description")}
              </p>
            </div>
            <span className="rounded border border-warning-border bg-warning-soft px-2 py-1 text-sm font-semibold text-warning-foreground">
              {t("quickLogin.badge")}
            </span>
          </div>
          <button
            type="button"
            onClick={submitDevelopmentLogin}
            disabled={isSubmitting}
            className="flex min-h-12 w-full items-center justify-between gap-3 rounded-md border border-input bg-background px-3 text-start text-sm shadow-sm transition-colors hover:bg-muted disabled:cursor-not-allowed disabled:opacity-60"
          >
            <span>
              <span className="block font-semibold text-foreground">
                {t("quickLogin.demoAdmin")}
              </span>
              <span className="mt-0.5 block text-sm text-muted-foreground">
                {t("quickLogin.workspace")}
              </span>
            </span>
            <span className="shrink-0 font-semibold text-primary">
              {submission === "development"
                ? t("quickLogin.submitting")
                : t("quickLogin.action")}
            </span>
          </button>
        </section>
      ) : null}
    </form>
  );
}
